import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import './Services.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Footer from '../../components/Footer';
import NextPageCTA from '../../components/NextPageCTA';
import BannerLogo from '../../components/BannerLogo';
import { 
  faDatabase, faBrain, faBalanceScale, faSearchDollar, 
  faChartLine, faBuilding, faClipboardCheck, faFileInvoiceDollar,
  faTools, faProjectDiagram, faUsers, faMapMarkedAlt,
  faAngleRight, faTimes, faHandPointer, faInfoCircle,
  faIndustry, faCity, faChevronLeft, faChevronRight,
  faDraftingCompass, faTruckLoading, faWrench,
  faShoppingCart, 
  faHardHat, 
  faFileContract 
} from '@fortawesome/free-solid-svg-icons';

function Services() {
  const [isVisible, setIsVisible] = useState(false);
  const [hoveredCard, setHoveredCard] = useState(null);
  const [expandedCards, setExpandedCards] = useState([]);
  const [animationClass, setAnimationClass] = useState('');
  const [currentSlide, setCurrentSlide] = useState(0);
  
  const carouselImages = [
    {
      src: require('../../assets/images/servicesbanner.webp'),
      text: "With over a decade of industry experience and a team of certified experts, we deliver specialized consulting and valuation services tailored to your business requirements."
    },
    {
      src: require('../../assets/images/servicesbanner.webp'),
      text: "Our comprehensive range of services covers everything from asset valuation to technical consultancy, ensuring your business gets the expert support it needs."
    },
    {
      src: require('../../assets/images/servicesbanner.webp'),
      text: "Trust our experienced professionals to provide accurate, reliable, and timely solutions for all your business valuation and consulting needs."
    }
  ];

  useEffect(() => {
    setIsVisible(true);
    
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('show');
        }
      });
    }, { threshold: 0.1 });

    document.querySelectorAll('.service-card, .section-title, .section-description').forEach((el) => {
      observer.observe(el);
    });

    // Carousel auto-play
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % carouselImages.length);
    }, 5000);

    return () => {
      document.querySelectorAll('.service-card, .section-title, .section-description').forEach((el) => {
        observer.unobserve(el);
      });
      clearInterval(interval);
    };
  }, []);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % carouselImages.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + carouselImages.length) % carouselImages.length);
  };

  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  const toggleExpandCard = (index, e) => {
    e.stopPropagation();
    e.preventDefault();

    // First, set animation class to out if there's an expanded card
    if (expandedCards.length > 0) {
      setAnimationClass('animate-out');
      
      // Wait for animation to complete before changing the expanded state
      setTimeout(() => {
        // If clicking the same card that's already expanded, collapse it
        if (expandedCards.includes(index)) {
          setExpandedCards([]);
        } else {
          // Otherwise, collapse any existing expanded card and expand the clicked one
          setExpandedCards([index]);
          setAnimationClass('animate-in');
        }
      }, 300); // Match this timing with CSS animation duration
    } else {
      // If no card is expanded, just expand and animate in
      setExpandedCards([index]);
      setAnimationClass('animate-in');
    }
  };

  const closeExpandedCard = (e) => {
    e.stopPropagation();
    setAnimationClass('animate-out');
    
    setTimeout(() => {
      setExpandedCards([]);
    }, 300);
  };

  const renderFormattedContent = (index) => {
    const content = getDetailedContent(index);
    
    // For the first service (Capitalization of Property, Plant and Equipment)
    if (index === 0) {
      return (
        <>
          <div className="service-expanded-title" style={{ 
            display: 'flex', 
            alignItems: 'center', 
            marginBottom: '20px', 
            paddingBottom: '15px', 
            borderBottom: '1px solid rgba(0, 0, 0, 0.05)' 
          }}>
            <div className="service-icon" style={{ 
              width: '50px', 
              height: '50px', 
              borderRadius: '10px', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center', 
              marginRight: '15px', 
              background: 'linear-gradient(135deg, #6a11cb, #2575fc)', 
              color: 'white', 
              fontSize: '20px', 
              boxShadow: '0 5px 15px rgba(106, 17, 203, 0.2)'
            }}>
              <FontAwesomeIcon icon={faBuilding} />
            </div>
            <h4 style={{ 
              margin: '0', 
              fontSize: '20px', 
              fontWeight: '600', 
              color: '#333', 
              flex: '1' 
            }}>{serviceData[index].title}</h4>
            <button className="close-expanded" onClick={closeExpandedCard} style={{ 
              position: 'absolute', 
              top: '20px', 
              right: '20px', 
              width: '36px', 
              height: '36px', 
              borderRadius: '50%', 
              background: 'white', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center', 
              cursor: 'pointer', 
              color: '#666', 
              boxShadow: '0 4px 10px rgba(0, 0, 0, 0.1)', 
              transition: 'all 0.3s ease', 
              border: 'none', 
              zIndex: '5' 
            }}>
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>
          
          <p style={{ marginBottom: '25px', color: '#555', lineHeight: '1.8', fontSize: '16px' }}>{content.intro}</p>
          
          <div className="aspects-container" style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', 
            gap: '30px', 
            marginBottom: '30px' 
          }}>
            <div className="aspect-section technical-aspects" style={{ 
              background: 'white', 
              padding: '20px', 
              borderRadius: '15px', 
              boxShadow: '0 5px 15px rgba(0, 0, 0, 0.05)'
            }}>
              <h5 style={{ marginTop: '0' }}><strong>Technical Aspects:</strong></h5>
              <ul style={{ 
                paddingLeft: '10px', 
                listStyleType: 'none' 
              }}>
                {content.technical.map((item, idx) => (
                  <li key={`tech-${idx}`} style={{ 
                    position: 'relative', 
                    padding: '8px 0 8px 30px', 
                    marginBottom: '10px', 
                    color: '#555', 
                    background: 'rgba(255, 255, 255, 0.7)', 
                    borderRadius: '10px', 
                    boxShadow: '0 4px 10px rgba(0, 0, 0, 0.03)', 
                    transition: 'all 0.3s ease',
                    position: 'relative',
                    fontSize: '16px'
                  }}>
                    <FontAwesomeIcon icon={faAngleRight} style={{
                      position: 'absolute',
                      left: '10px',
                      top: '12px',
                      color: '#6a11cb'
                    }} />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            
            <div className="aspect-section commercial-aspects" style={{ 
              background: 'white', 
              padding: '20px', 
              borderRadius: '15px', 
              boxShadow: '0 5px 15px rgba(0, 0, 0, 0.05)'
            }}>
              <h5 style={{ marginTop: '0' }}><strong>Commercial Aspects:</strong></h5>
              <ul style={{ 
                paddingLeft: '10px', 
                listStyleType: 'none' 
              }}>
                {content.commercial.map((item, idx) => (
                  <li key={`comm-${idx}`} style={{ 
                    position: 'relative', 
                    padding: '8px 0 8px 30px', 
                    marginBottom: '10px', 
                    color: '#555', 
                    background: 'rgba(255, 255, 255, 0.7)', 
                    borderRadius: '10px', 
                    boxShadow: '0 4px 10px rgba(0, 0, 0, 0.03)', 
                    transition: 'all 0.3s ease',
                    position: 'relative',
                    fontSize: '16px'
                  }}>
                    <FontAwesomeIcon icon={faAngleRight} style={{
                      position: 'absolute',
                      left: '10px',
                      top: '12px',
                      color: '#00c2cb'
                    }} />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </>
      );
    }
    
    // For all other services
    return (
      <>
        <div className="service-expanded-title" style={{ 
          display: 'flex', 
          alignItems: 'center', 
          marginBottom: '20px', 
          paddingBottom: '15px', 
          borderBottom: '1px solid rgba(0, 0, 0, 0.05)' 
        }}>
          <div className="service-icon" style={{ 
            width: '50px', 
            height: '50px', 
            borderRadius: '10px', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            marginRight: '15px', 
            background: index === 0 ? 'linear-gradient(135deg, #6a11cb, #2575fc)' :
                      index === 1 ? 'linear-gradient(135deg, #00c2cb, #2575fc)' :
                      index === 2 ? 'linear-gradient(135deg, #FF8008, #FFC837)' :
                      index === 3 ? 'linear-gradient(135deg, #11998e, #38ef7d)' :
                      index === 4 ? 'linear-gradient(135deg, #FF512F, #DD2476)' :
                      'linear-gradient(135deg, #00c2cb, #2575fc)', 
            color: 'white', 
            fontSize: '20px', 
            boxShadow: index === 0 ? '0 5px 15px rgba(106, 17, 203, 0.2)' :
                      index === 1 ? '0 5px 15px rgba(0, 194, 203, 0.2)' :
                      index === 2 ? '0 5px 15px rgba(255, 128, 8, 0.2)' :
                      index === 3 ? '0 5px 15px rgba(17, 153, 142, 0.2)' :
                      index === 4 ? '0 5px 15px rgba(221, 36, 118, 0.2)' :
                      '0 5px 15px rgba(0, 194, 203, 0.2)' 
          }}>
            <FontAwesomeIcon icon={serviceData[index].icon} />
          </div>
          <h4 style={{ 
            margin: '0', 
            fontSize: '20px', 
            fontWeight: '600', 
            color: '#333', 
            flex: '1' 
          }}>{serviceData[index].title}</h4>
          <button className="close-expanded" onClick={closeExpandedCard} style={{ 
            position: 'absolute', 
            top: '20px', 
            right: '20px', 
            width: '36px', 
            height: '36px', 
            borderRadius: '50%', 
            background: 'white', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            cursor: 'pointer', 
            color: '#666', 
            boxShadow: '0 4px 10px rgba(0, 0, 0, 0.1)', 
            transition: 'all 0.3s ease', 
            border: 'none', 
            zIndex: '5' 
          }}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
        
        {content.intro && <p style={{ marginBottom: '25px', color: '#555', lineHeight: '1.8', fontSize: '16px' }}>{content.intro}</p>}
        
        {content.list && (
          <ul style={{ 
            paddingLeft: '10px', 
            listStyleType: 'none',
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '20px'
          }}>
            {content.list.map((item, idx) => (
              <li key={`list-${idx}`} style={{ 
                position: 'relative', 
                padding: '12px 15px 12px 35px', 
                marginBottom: '10px', 
                color: '#555', 
                background: 'rgba(255, 255, 255, 0.7)', 
                borderRadius: '10px', 
                boxShadow: '0 4px 10px rgba(0, 0, 0, 0.03)', 
                transition: 'all 0.3s ease',
                position: 'relative',
                fontSize: '16px'
              }}>
                <FontAwesomeIcon icon={faAngleRight} style={{
                  position: 'absolute',
                  left: '12px',
                  top: '16px',
                  color: '#00c2cb'
                }} />
                {item}
              </li>
            ))}
          </ul>
        )}
      </>
    );
  };

  const getDetailedContent = (index) => {
    switch(index) {
      case 'compCard1':
        return {
          intro: "Schedule II to the 2013 Act enshrines within itself the principle for recognizing depreciation on the assets over their useful lives and provides as follows:",
          list: [
            "Part C of the Schedule II to the 2013 Act lays down indicative useful lives of certain tangible assets.",
            "The principle focuses on systematic allocation of the depreciable amount of an asset over its useful life."
          ]
        };
      case 'compCard2':
        return {
          intro: "Key guidelines regarding useful life and residual value under the Companies Act, 2013:",
          list: [
            "Useful life of tangible assets should not be ordinarily different from the useful life specified in Part C of the Schedule II to the 2013 Act.",
            "The residual value should not be more than 5% of the original cost of the tangible assets.",
            "Proper documentation must be maintained for all assessments of useful life and residual value."
          ]
        };
      case 'compCard3':
        return {
          intro: "Technical assessment requirements for companies adopting different useful life or residual values:",
          list: [
            "Where a company adopts a different useful life or uses a different residual value as prescribed in the Act, the company is required to disclose such differences.",
            "Justification must be provided in the financial statements that is duly supported by a technical advice.",
            "The company should involve technical experts to determine the useful life of the asset.",
            "Adequate details about the technical assessment of the useful lives of the tangible assets must be maintained."
          ]
        };
      case 'compCard4':
        return {
          intro: "Component Definition as per Schedule II to the 2013 Act:",
          list: [
            "The Schedule II to the 2013 Act requires that useful life and depreciation for significant components of an asset should be determined separately.",
            "A component is defined as a part of an asset which has a significant value and a different useful life.",
            "This approach provides a more accurate representation of asset depreciation."
          ]
        };
      case 'compCard5':
        return {
          intro: "Assessment of components involves several key considerations:",
          list: [
            "Identification of components requires a careful assessment of the facts and circumstances.",
            "Technical evaluation is necessary to determine which parts of an asset constitute significant components.",
            "Assessment should consider both the value component and its differing useful life."
          ]
        };
      case 'compCard6':
        return {
          intro: "Regulatory requirements for componentization:",
          list: [
            "Ministry of corporate affairs (MCA) has made the component approach mandatory for financial year commencing on or after 1 April 2015.",
            "This regulatory requirement aims to improve financial reporting accuracy.",
            "Companies must establish systems and processes to track components and their depreciation separately."
          ]
        };
      case 0:
        return {
          intro: "We help organizations ensure that capital expenditures are correctly classified and capitalized, in line with IFRS and national or local accounting standards. Proper capitalization affects depreciation, asset recognition, and financial reporting accuracy. Our services help clients maintain clean, auditable records that withstand internal and regulatory scrutiny. This process also improves investor confidence by aligning with transparent accounting practices.",
          technical: [
            "Complete understanding of:Products/by-products and the key inputs.",
            "Process & Process flow.",
            "Capacity of the plant.",
            "Configuration of various units of the plant.",
            "Operational aspects of various units.",
            "Study of Engineering drawings of each unit and confirm the bill of quantity.",
            "Confirm that the bill of quantity of items of each unit has been physically installed.",
            "Preparation of unit-wise List of Items indicating Source of Supply, Major Technical Specifications, Quantities etc.",
            "Identifying the item from Sch II of the Companies Act 2013 for Useful Life and Residual Value."
          ],
          commercial: [
            "Review of Purchase Orders, Contracts & Agreements to identify item-wise costs.",
            "Review of commercial files.",
            "Categorization of line items in the commercial file (Asset- wise & unit-wise).",
            "Identifying all direct costs such as Supply Cost, License Fees, Consultancy Charges, Taxes & Duties etc.",
            "Componentization of items which have a substantial value and different useful life.",
            "Preparation of Fixed Assets Register with Tag Nos and all the above details."
          ]
        };
      case 1:
        return {
          intro: "Our asset valuation services rely on globally accepted methods and are aligned with regulatory frameworks, ensuring financial transparency. These valuations are critical for financial reporting, mergers, impairment testing, and audits under IFRS/accounting practices. By establishing fair asset values, we support strategic decision-making and compliance. Each valuation is backed by documentation and traceability, enabling audit readiness.",
          list: [
            "Site Visit for Physical Observation.",
            "Selection of appropriate Valuation Approach, namely Market Approach, Cost Approach or Income Approach.",
            "Obtain historical costs, if available.",
            "Consider factors for Obsolescence (Physical/ Technical/ Economic)."
          ]
        };
      case 2:
        return {
          intro: "We perform thorough physical verification of fixed assets and inventories to ensure alignment with accounting records. This exercise strengthens internal controls and supports compliance with audit and regulatory requirements. Verified assets are tagged, located, and reconciled with the books to reduce ghost assets and leakages. Our process ensures traceability and enhances the credibility of financial disclosures.",
          list: [
            "Site visit for Physical Observation.",
            "Verification of both types of inventories, namely Project Inventory and store Inventory",
            "Reconciliation of Assets with Customer's Fixed Assets Register (FAR).",
            "Supply of Metallic or Paper Tags for Assets based on Customer Requirement"
          ]
        };
      case 3:
        return {
          intro: "We evaluate the remaining useful life of assets based on physical inspections, usage data, and adherence to technical norms. This helps ensure depreciation is charged in accordance with IFRS and other standards. Our insights allow for better asset lifecycle planning, replacement scheduling, and impairment testing. Accurate life assessment is essential to avoid under- or over-depreciation risks.",
          list: [
            "Site Visit for Physical Inspection.",
            "Study of Maintenance Records",
            "Study of Production Records",
            "Statistical Evaluation"
          ]
        };
      case 4:
        return {
          intro: "We deliver comprehensive business and plant valuations grounded in industry benchmarks and financial regulations. These valuations are often used in investor reporting, restructuring, or financial consolidation under IFRS guidelines. We provide substantiated reports that support funding, acquisition, or exit decisions. Our approach ensures fair representation of asset value and enterprise worth.",
          list: [
            "Any asset can be valued, but some assets are easier to value than others and the details of valuation will vary from case to case.",
            "Valuation of a real estate property requires different information and follows a different format than the valuation of a publicly traded stock.",
            "VRPL has carried out valuations of several businesses and assets.",
            "Our panel of industry experts having wealth of working experience in industries ranging from Steel, Power, Construction, Paper, Shipyards, Automobile etc has carried out independent studies on behalf of various clients."
          ]
        };
      case 5:
        return {
          intro: "Our team conducts impairment testing to assess whether the recoverable amount of an asset falls below its carrying value. This is mandatory under IFRS and national accounting codes, especially for long-lived or capital-intensive assets. We support clients in identifying triggers for impairment, and preparing disclosures. The findings help maintain accurate and compliant financial statements.",
          list: [
            "Intangible assets with indefinite lives are also tested annually for impairment. The disclosures in relation to impairment are extensive due to the number and sensitivity of estimates and assumptions used by the management as the basis for their review.",
            "AS-28 deals with the impairment of assets i.e. the carrying amount of the assets should not be more than the recoverable amount of the assets. This calculation must be done at the end of each financial year.",
            "VRPL has carried out several impairment studies on behalf of its clients engaged in the business of Steel, Power, Construction, Ship building, Paper, Automobile components etc.",
            "VRPL has the requisite expertise to carry out independent study in identifying the Cash Generating Units, verify the projected cash flows and to issue a report on the impairment of the asset (s)."
          ]
        };
      case 6:
        return {
          intro: "We offer strategic consultancy to assess technical viability and financial alignment with regulatory and accounting frameworks. Our services help organizations plan large-scale investments, optimize operating models, and stay compliant. We ensure that funding structures, capital allocation, and project execution are financially disciplined and feasible. This is particularly useful in regulatory filings and investor evaluations.",
          list: [
            "Providing assistance to existing and new units in project report preparation, financial structuring, financial analysis & fund raising (Debt, Equity & Hybrid).",
            "Drawing up of implementation schedule.",
            "Assistance during trial run of the plant.",
            "Complete Support services in turn-around management of business units or divisions.",
            "Performance and profitability improvement.",
            "Restructuring of operations.",
            "Management Capabilities for project management.",
            "Relocating operations and change management.",
            "Due diligence, risk assessment and risk management."
          ]
        };
      case 7:
        return {
          intro: "Our feasibility studies provide comprehensive insights into the practicality and profitability of a proposed investment. We evaluate technical soundness, financial projections, and asset utility against industry and regulatory benchmarks. These studies support funding decisions, joint ventures, and M&A planning. They provide justification for investment through actionable data and outcome-based recommendations.",
          list: [
            "If a project is seen to be feasible from the results of the study, the next logical step is to proceed with the full business plan. The research and information uncovered in the feasibility study will support the business planning stage and reduce the research time. The cost of the business plan is also reduced.",
            "A thorough viability analysis provides an abundance of information that is also necessary for the business plan. A feasibility study should contain clear supporting evidence for its recommendations. The strength of the recommendations can be weighed against the study of ability to demonstrate the continuity that exists between the research analysis and the proposed business model.",
            "A Business Feasibility Study is heavily dependent on the market research and analysis.",
            "VRPL can undertake Technical Feasibility study for potential investors and stakeholders."
          ]
        };
      case 8:
        return {
          intro: "We provide independent project monitoring to track execution progress, adherence to milestones, and vendor performance. This service ensures accountability and traceability as per project governance frameworks. Our reports support financial and regulatory audits by verifying timelines, deliverables, and budget adherence. This reduces the risk of cost overruns and compliance failures.",
          list: [
            "Services are offered to the clients in the areas of Preparation of Project brief, Project Programming including defining Milestones and Deliverables, Procurement Strategy, Selection of Vendors/ Suppliers/ Contractors, Project Time Management, Materials Management, Construction/ Installation Supervision, Site Staffing, Site Administration, Monitor Project Construction programs, Quality Control, Reporting, Commissioning & Close Out.",
            
          ]
        };
      case 9:
        return {
          intro: "We act as independent evaluators for project owners and financial institutions by validating whether capital spend is justified and effective. Our team ensures that investments meet both technical specifications and expected outcomes. This includes cost validation, asset review, and execution quality checks, aligning with IFRS requirements for asset recognition. Lenders and owners use our services to ensure funds are spent appropriately and that projects offer measurable value.",
          list: [
            "The services offered include Preparation of Tender Documents in Consultation with the owner, Attending Pre-bid Meetings, Bid Evaluation and Contract Finalization, Review Engineering, Inspection Services, Project Management, Field Services, Engineering Services for Testing, Commissioning and Performance Tests, Project Closure, Other components of Lender's/ Owner's Engineer's scope.",
            "VRPL has a team of senior management who can assist the Lenders/ Owner in all aspects of setting up the plant."
          ]
        };
      case 10:
        return {
          intro: "We perform asset audits, compliance checks, and performance reviews to validate operational standards and financial health. These inspections support audit preparation and financial disclosures required by regulators and accounting standards. Our services help flag deviations, inefficiencies, or gaps in documentation. Organizations use our findings to strengthen governance and improve internal accountability.",
          list: [
            "Evaluation and Selection of assets",
            "Performance improvement",
            "Quality Management Systems & Operation Management Studies",
            "International Quality Certification",
            "Regulatory compliances",
            "Investment proposals",
            "Technical due diligence",
            "Financial due diligence",
            "Engineering and Risk assessment",
            "Technical Assessment of Useful Life of Assets and Residual Value",
            "Inspection services & Third party inspection services",
            "Review of material test reports Mechanical tests",
            "Welder's performance qualifications",
            "NDE and performing visual inspections",
            "Verification of shipping quantities including proper packing & marking",
            "Selection of specialized manpower and training"
          ]
        };
      case 11:
        return {
          intro: "Our site-level inspections evaluate operational readiness, risk exposure, and compliance health, Asset verifications, etc. These reviews support due diligence and regulatory requirements under accounting standards. Findings from these visits feed into asset registers, impairment studies, and lifecycle management. They are key to ensuring physical and financial records align, providing assurance to stakeholders.",
          list: [
            "Pre-audit and understanding the manufacturing/production capability and assessment of the overall strategic strength is carried out by companies before take over or purchase of specific assets of the company.",
            "In the present scenario of the industry where brown-field expansion is the norm for faster growth, plant visits help build a better understanding of the performance potential and the current challenges and deficits.",
            "Traditional reports rarely present an up-to-date, thorough picture of an operation's performance. Financial information tends to give an outdated picture of operational health: it will often reflect a plant's performance as it was a year or so ago.",
            "VRPL assists in carrying out such visits and inspection services for plants located within and outside India."
          ]
        };
      default:
        return null;
    }
  };

  const serviceData = [
    {
      title: "Capitalization of Property, Plant and Equipment",
      icon: faBuilding,
      color: "gradient-blue",
      description: "Comprehensive capitalization services for Property, Plant & Equipment assets that provide economic benefits to an enterprise for a period greater than that covered by current year's financial statements."
    },
    {
      title: "Valuation of Fixed Assets",
      icon: faSearchDollar,
      color: "gradient-green",
      description: "Site Visit for Physical Observation. Selection of appropriate Valuation Approach (Market Approach/ Cost Approach/ Income Approach)."
    },
    {
      title: "Physical Verification of Fixed Assets and Inventory",
      icon: faClipboardCheck,
      color: "gradient-purple",
      description: "Thorough on-site verification of assets with detailed reconciliation against existing fixed asset registers and optional tagging services."
    },
    {
      title: "Technical Assessment of Balance Useful Life",
      icon: faBalanceScale,
      color: "gradient-orange",
      description: "Site Visit for Physical Inspection. Study of Maintenance Philosophy/ Production Records/ Product Quality Records/ Statistical Evaluation."
    },
    {
      title: "Business and Plant Valuations",
      icon: faIndustry,
      color: "gradient-red",
      description: "Every asset, financial as well as real, has a value. The key to successful investing in and managing these assets lies in understanding not only what the value is but also the source of the value."
    },
    {
      title: "Asset Impairment Studies",
      icon: faChartLine,
      color: "gradient-teal",
      description: "Impairment requirements under IFRS are more stringent than many local GAAPS, with goodwill being tested annually for impairment."
    },
    {
      title: "Technical and Financial Consultancy",
      icon: faTools,
      color: "gradient-pink",
      description: "Providing assistance to existing and new units in project report preparation, financial structuring, financial analysis & fund raising (Debt, Equity & Hybrid)."
    },
    {
      title: "Technical and Financial Feasibility Studies",
      icon: faFileInvoiceDollar,
      color: "gradient-indigo",
      description: "Only one out of fifty business ideas may be commercially viable. A business feasibility study is an effective way to safeguard against wastage of further investment or resources."
    },
    {
      title: "Project Monitoring",
      icon: faProjectDiagram,
      color: "gradient-amber",
      description: "Services are offered to the clients in the areas of Preparation of Project brief, Project Programming including defining Milestones and Deliverables, Procurement Strategy."
    },
    {
      title: "Lender's/Owner's Engineers",
      icon: faUsers,
      color: "gradient-cyan",
      description: "VRPL provides professional engineering consultancy services to cover various works related to any project."
    },
    {
      title: "Management & Inspection Services",
      icon: faBrain,
      color: "gradient-deepPurple",
      description: "VRPL provides assistance to companies in identifying and mitigating risks in the operational performance of the company."
    },
    {
      title: "Site Visits & Strategic Inspections",
      icon: faMapMarkedAlt,
      color: "gradient-lime",
      description: "VRPL provides site visits for specific requirements of the client such as for Physical Audit, Componentization, Business Assessment etc."
    }
    // {
    //   title: "Technical Assessment",
    //   icon: faFileInvoiceDollar,
    //   color: "gradient-orange",
    //   description: "CWIP (Capital Work in Progress) data is obtained from Customer. Above data is categorised into Direct Costs & Indirect Costs. The costs are further classified to equipment level."
    // }
  ];

  // Add this function to organize cards into rows
  const organizeIntoRows = (items, columnsPerRow = 3) => {
    const rows = [];
    for (let i = 0; i < items.length; i += columnsPerRow) {
      rows.push(items.slice(i, i + columnsPerRow));
    }
    return rows;
  };

  return (
    <div className="services-page">
      {/* Services Hero Section */}
      <div className="services-hero">
        <img 
          src={require('../../assets/images/servicesbg.webp')} 
          alt="Services Banner" 
          className="hero-image" 
        />
        <div className="hero-overlay"></div>
        <h1 className="services-hero-title">Our Services</h1>
        <BannerLogo />
        <div className="hero-content">
          <p className="hero-text">
            With over a decade of industry experience and a team of certified experts,<br />
            we deliver specialized consulting and valuation services tailored to your<br />
            business requirements.
          </p>
        </div>
      </div>

      {/* Main Services Content */}
      <section id="services-content" className={`${isVisible ? 'visible' : ''}`}>
        <div className="container">
          {/* Componentization, Estimation of Useful Life & Residual Value */}
          <div className="section-header">
            <div className="title-container">
              <h3 className="section-title">Capitalization</h3>
            </div>
          </div>
          
          {/* Cards for Componentization, Estimation of Useful Life & Residual Value */}
          <div className="component-section first-component-section">
            <div className="component-section-bg first-component-bg"></div>
            <div className="services-grid">
              <div className="service-card-container">
                <div className="service-card gradient-orange">
                  <div className="card-content">
                    <div className="service-icon">
                      <FontAwesomeIcon icon={faFileInvoiceDollar} />
                    </div>
                    <h4 style={{ fontSize: '20px' }}>Requirement under Companies Act 2013</h4>
                    <p style={{ padding: '0 15px', fontSize: '16px', textAlign: 'left' }}>
                      • Componentisation<br/>
                      • Allocation of Direct & Indirect Costs<br/>
                      • Indication of Total Useful Life
                    </p>
                  </div>
                </div>
              </div>

              <div className="service-card-container">
                <div className="service-card gradient-blue">
                  <div className="card-content">
                    <div className="service-icon">
                      <FontAwesomeIcon icon={faBalanceScale} />
                    </div>
                    <h4 style={{ fontSize: '20px' }}>Componentisation</h4>
                    <p style={{ padding: '0 15px', fontSize: '16px', textAlign: 'left' }}>
                      • A component is defined as a part of an asset which has a significant value and a different useful life than the main asset<br/>
                      • Technical evaluation is necessary to determine which parts of an asset constitute significant components<br/>
                      • Ministry of corporate affairs (MCA) has made the component approach mandatory for financial years commencing on or after 1 April 2015<br/>
                      • This regulatory requirement aims to improve financial reporting accuracy
                    </p>
                  </div>
                </div>
              </div>

              <div className="service-card-container">
                <div className="service-card gradient-green">
                  <div className="card-content">
                    <div className="service-icon">
                      <FontAwesomeIcon icon={faClipboardCheck} />
                    </div>
                    <h4 style={{ fontSize: '20px' }}>Allocation of Direct & Indirect Costs</h4>
                    <p style={{ padding: '0 15px', fontSize: '16px', textAlign: 'left' }}>
                      • CWIP (Capital Work in Progress) data is obtained from Customer<br/>
                      • Above data is categorised into Direct Costs & Indirect Costs<br/>
                      • The costs are further classified to equipment level
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Componentization Section */}
          <div className="section-header">
            <div className="title-container">
              {/* <h3 className="section-title">Componentization</h3> */}
            </div>
          </div>
          
          {/* Cards for Componentization */}
          <div className="component-section second-component-section">
            <div className="component-section-bg second-component-bg"></div>
            <div className="services-grid">
              <div className="service-card-container">
                <div 
                  className="service-card-gradient-orange-componentization"
                >
                  <div className="card-content-for-componentization">
                    <div className="service-icon">
                      <FontAwesomeIcon icon={faProjectDiagram} />
                    </div>
                    <h4 style={{ fontSize: '20px' }}>Indication of Total Useful Life</h4>
                    <p style={{ padding: '0 15px', fontSize: '16px', textAlign: 'left' }}>
                      • Schedule II of the Companies Act 2013 enshrines within itself the principle for recognizing depreciation on the assets over their useful lives<br/>
                      • Part- C/ Schedule- II of Companies Act 2013 lays down indicative useful lives of certain tangible assets<br/>
                      • Useful life of tangible assets should not be ordinarily different from the useful life specified in Companies Act 2013<br/>
                      • Residual value should not be more than 5% of the original cost of the tangible assets
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Services Section Header */}
          <div className="services-main-grid-container">
            <div className="services-main-grid-bg"></div>
            <div className="section-header">
              <div className="title-container">
                <h3 className="section-title">OUR SERVICES</h3>
              </div>
            </div>
            
            {/* Services Grid */}
            <div className="services-grid services-main-grid">
              {organizeIntoRows(serviceData, 3).map((row, rowIndex) => (
                <React.Fragment key={`row-${rowIndex}`}>
                  {row.map((service, colIndex) => {
                    const index = rowIndex * 3 + colIndex;
                    return (
                      <div key={`service-${index}`} className="service-card-container">
                        <div 
                          className={`service-card ${service.color}`}
                          style={{ cursor: 'pointer', height: '100%', display: 'flex', flexDirection: 'column' }}
                        >
                          <div className="card-content" style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                            <div className="service-icon">
                              <FontAwesomeIcon icon={service.icon} />
                            </div>
                            <h4 style={{ fontSize: '20px' }}>{service.title}</h4>
                            <p style={{ 
                              padding: '0 15px',
                              marginBottom: '10px', 
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              maxHeight: '120px',
                              fontSize: '16px'
                            }}>{service.description}</p>
                            <div style={{ 
                              textAlign: 'center', 
                              padding: '10px 0',
                              width: '100%',
                              marginTop: 'auto',
                              position: 'relative',
                              zIndex: 5
                            }}>
                              <button 
                                className="show-more-btn"
                                onClick={(e) => toggleExpandCard(index, e)}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  gap: '10px',
                                  padding: '12px 25px',
                                  background: 'rgba(0, 194, 203, 0.2)',
                                  border: '2px solid rgba(0, 194, 203, 0.5)',
                                  borderRadius: '50px',
                                  color: '#00c2cb',
                                  fontWeight: '700',
                                  transition: 'all 0.3s ease',
                                  cursor: 'pointer',
                                  fontSize: '1rem',
                                  width: '90%',
                                  boxShadow: '0 4px 10px rgba(0, 0, 0, 0.1)',
                                  margin: '0 auto',
                                  textTransform: 'uppercase',
                                  letterSpacing: '0.5px'
                                }}
                              >
                                <FontAwesomeIcon icon={faInfoCircle} className="info-icon" />
                                {expandedCards.includes(index) ? "Hide Details" : "View Details"} 
                                <FontAwesomeIcon icon={faAngleRight} />
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  
                  {/* Add expanded details at the end of each row */}
                  {row.some((_service, colIndex) => {
                    const index = rowIndex * 3 + colIndex;
                    return expandedCards.includes(index);
                  }) && (
                    <div 
                      className={`service-details-expanded ${animationClass}`}
                      data-service={expandedCards.find(index => Math.floor(index / 3) === rowIndex)}
                      style={{
                        gridColumn: '1 / -1',
                        marginTop: '20px',
                        marginBottom: '40px',
                        padding: '0',
                        background: '#ffffff',
                        borderRadius: '20px',
                        boxShadow: '0 15px 40px rgba(0, 0, 0, 0.08)',
                        color: '#333333',
                        lineHeight: '1.8',
                        width: '100%',
                        position: 'relative',
                        zIndex: '10',
                        overflow: 'hidden',
                        border: 'none',
                        display: 'block',
                        opacity: '1',
                        height: 'auto',
                        minHeight: '200px'
                      }}
                    >
                      <div className="expanded-content" style={{ padding: '30px', position: 'relative', zIndex: '2' }}>
                        {renderFormattedContent(expandedCards.find(index => 
                          Math.floor(index / 3) === rowIndex
                        ))}
                      </div>
                    </div>
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>
      </section>
      
      <NextPageCTA 
        title="Venture into our latest Engineering, Procurement, and Construction Solar-Division" 
        description="Discover our comprehensive Solar EPC solutions that streamline your projects from concept to completion"
        nextPage={{
          path: "/epc",
          label: "Venture into Solar EPC"
        }}
        headingText="Venture into our latest iteration"
      />
      <Footer />
    </div>
  );
}

export default Services; 